import * as React from 'react'

import { TagColor as Color, Tag } from '@factorialco/deprecated-design-system'
import { StatusVariant } from '@factorialco/f0-react'
import {
  DocumentsSignatureStatusEnum,
  PerformanceAgreementStatusEnum,
  PerformanceAgreementStatusTagFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import {
  DOCUMENT_SIGNATURE_BOUNCED_EMAIL,
  DOCUMENT_SIGNATURE_CANCELLED,
  DOCUMENT_SIGNATURE_DECLINED,
  DOCUMENT_SIGNATURE_ERROR,
  DOCUMENT_SIGNATURE_EXPIRED,
  DOCUMENT_SIGNATURE_PARTIALLY_SIGNED,
} from 'modules/documents/lib/constants'

export type AgreementStatus =
  | PerformanceAgreementStatusEnum
  | DocumentsSignatureStatusEnum
  | 'pending_signatures'

interface AgreementStatusTagProps {
  agreement: PerformanceAgreementStatusTagFragment
  suffix?: string
}

const agreementStatusToColor: Record<AgreementStatus, Color> = {
  pending: Color.malibu,
  signed: Color.correct,
  pending_signatures: Color.champagne,
  partially_signed: Color.malibu,
  completed: Color.correct,
  declined: Color.radical,
  bounced_email: Color.radical,
  cancelled: Color.neutral,
  error: Color.radical,
  expired: Color.radical,
}

export const agreementStatusToVariant: Record<AgreementStatus, StatusVariant> = {
  pending: 'warning',
  signed: 'positive',
  pending_signatures: 'warning',
  partially_signed: 'info',
  completed: 'positive',
  declined: 'critical',
  bounced_email: 'critical',
  cancelled: 'neutral',
  error: 'critical',
  expired: 'critical',
}

export const agreementStatusLabel = (status: AgreementStatus) => {
  switch (status) {
    case 'pending':
      return i18n.t('performance_process.agreements.my_agreements.category_pending_submission')
    case 'signed':
      return i18n.t('performance_process.agreements.status.submitted')
    case 'pending_signatures':
      return i18n.t('performance_process.agreements.status.pending_signatures')
    case 'partially_signed':
      return DOCUMENT_SIGNATURE_PARTIALLY_SIGNED()
    case 'completed':
      return i18n.t('performance_process.agreements.status.signed')
    case 'declined':
      return DOCUMENT_SIGNATURE_DECLINED()
    case 'bounced_email':
      return DOCUMENT_SIGNATURE_BOUNCED_EMAIL()
    case 'cancelled':
      return DOCUMENT_SIGNATURE_CANCELLED()
    case 'error':
      return DOCUMENT_SIGNATURE_ERROR()
    case 'expired':
      return DOCUMENT_SIGNATURE_EXPIRED()
    default:
      const _exhaustiveCheck: never = status
      throw new Error(`Unhandled agreement status: ${_exhaustiveCheck}`)
  }
}

export const agreementStatus = (agreement: PerformanceAgreementStatusTagFragment) => {
  const signatureStatus = agreement.agreementDocuments?.[0]?.signatureStatus
  return signatureStatus === 'pending' ? 'pending_signatures' : signatureStatus || agreement.status
}

export const AgreementStatusTag = ({ agreement, suffix }: AgreementStatusTagProps) => {
  const status = agreementStatus(agreement)
  const label = agreementStatusLabel(status)
  const color = agreementStatusToColor[status]

  return (
    <Tag inline capitalize type={color}>
      {label} {suffix}
    </Tag>
  )
}
